<template>
  <v-col data-testid="gate-feed-page-table">
    <v-row class="d-flex align-center justify-space-between">
      <v-col>
        <v-row class="d-flex align-center gap-3">
          <text-field
            v-on:keyup.enter="search"
            variant="outlined"
            autofocus
            clearable
            clear-icon="mdi-close"
            v-model="filterOptions.searchStr"
            height="30"
            data-testid="outer-search-input-wrapper"
            placeholder="Search this table..."
            hide-details="auto"
            prepend-inner-icon="mdi-magnify" />
          <primary-button data-testid="outer-search" @click="search">Search</primary-button>
        </v-row>
      </v-col>
      <v-col>
        <v-select
          variant="outlined"
          color="primary"
          v-model="selectedGate"
          :disabled="!selectedWarehouse"
          :items="gates"
          item-title="name"
          item-value="id"
          placeholder="Gate"
          :return-object="false"
          density="compact"
          :clearable="true"
          data-testid="gate-feed-gate-select"
          hide-details />
      </v-col>
      <v-col>
        <v-select
          variant="outlined"
          color="primary"
          v-model="selectedDirection"
          :disabled="!selectedWarehouse"
          :items="directions"
          item-title="name"
          item-value="id"
          placeholder="Direction"
          :return-object="false"
          density="compact"
          :clearable="true"
          data-testid="gate-feed-direction-select"
          hide-details />
      </v-col>
      <v-col>
        <date-range
          clearable
          label="Date range"
          :show-day-count="false"
          outlined
          :initial-range="[filterOptions.startDate, filterOptions.endDate]"
          ref="dateRange"
          data-testid="gate-feed-date-select"
          @update:model-value="dateRangeChanged"></date-range>
      </v-col>
      <v-col>
        <v-select
          v-model="selectedColumns"
          :items="columns"
          placeholder="Table columns"
          variant="outlined"
          density="compact"
          :clearable="true"
          data-testid="gate-feed-columns-select"
          hide-details
          multiple>
          <template v-slot:prepend-item>
            <v-list-item title="Select All" @click="toggle">
              <template v-slot:prepend>
                <v-checkbox-btn
                  :color="selectedSomeColumns ? 'indigo-darken-4' : undefined"
                  :indeterminate="selectedSomeColumns && !selectedAllColumns"
                  :model-value="selectedAllColumns"></v-checkbox-btn>
              </template>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:selection="{ item, index }">
            <span v-if="index < 1">{{ item.title }}</span>
            <span v-if="index === 1" class="text-grey text-caption align-self-center">
              (+{{ selectedColumns.length - 1 }} others)
            </span>
          </template>
        </v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table-server
          data-testid="gate-feed-data-table"
          :headers="selectedColumns"
          :items="observationsList"
          :items-length="totalItems"
          ref="gateFeedTable"
          :items-per-page-options="[10, 25, 50, 100]"
          item-key="id"
          :page="page"
          :footer-props="footerProps"
          class="gate-feed-table"
          :search="filterOptions.searchStr">
          <template v-slot:item.images="{ item }">
            <v-img
              v-if="item.images && item.images.length > 0"
              :src="item.images[0]"
              width="130"
              height="80"
              cover />
          </template>
          <template v-slot:item.checkin="{ item }">
            <v-chip
              v-if="item.checkin"
              color="green"
              size="small"
              variant="tonal"
              class="font-weight-bold">
              YES
            </v-chip>
            <v-chip v-else color="red" size="small" variant="tonal" class="font-weight-bold">
              NO
            </v-chip>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { defineComponent, ref, shallowRef, computed } from 'vue';
import { DateTime } from 'luxon';

import useGateFeed from '../composables/useGateFeed';

const DEFAULT_DATE_RANGE_DAYS = 7;

export default defineComponent({
  name: 'GateFeedPageTable',
  components: {},
  emits: [],
  props: {
    observations: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const gateFeedStore = useGateFeed();

    const gates = [
      { id: 'gate1', name: 'Gate 1' },
      { id: 'gate2', name: 'Gate 2' },
      { id: 'gate3', name: 'Gate 3' }
    ];
    const directions = [
      { id: 'inbound', name: 'Inbound' },
      { id: 'outbound', name: 'Outbound' }
    ];
    const columns = [
      // General columns
      { title: 'Picture', value: 'images', category: 'general' },
      { title: 'Check-in', value: 'checkin', category: 'general' },
      { title: 'Date', value: 'detectedAt', category: 'general' },
      { title: 'Time', value: 'detectedAt', category: 'general' },
      { title: 'Direction', value: 'direction', category: 'general' },
      // Vehicle columns
      { title: 'Vehicle dwell time', value: 'vehicle.dwellTime', category: 'vehicle' },
      { title: 'Vehicle LP', value: 'vehicle.licensePlate', category: 'vehicle' },
      { title: 'Vehicle number', value: 'vehicle.number', category: 'vehicle' },
      { title: 'Carrier', value: 'vehicle.carrier', category: 'vehicle' },
      { title: 'DOT number', value: 'vehicle.dotNumber', category: 'vehicle' },
      { title: 'Vehicle class', value: 'vehicle.class', category: 'vehicle' },
      { title: 'Vehicle fuel type', value: 'vehicle.fuelType', category: 'vehicle' },
      { title: 'Vehicle damage', value: 'vehicle.damage', category: 'vehicle' },
      // Chassis columns
      { title: 'Chassis dwell time', value: 'chassis.dwellTime', category: 'chassis' },
      { title: 'Chassis number', value: 'chassis.number', category: 'chassis' },
      { title: 'Chassis LP', value: 'chassis.licensePlate', category: 'chassis' },
      { title: 'Is bare', value: 'chassis.isBare', category: 'chassis' },
      { title: 'Chassis damage', value: 'chassis.damage', category: 'chassis' },
      // Trailer columns
      { title: 'Trailer dwell time', value: 'trailer.dwellTime', category: 'trailer' },
      { title: 'Trailer LP', value: 'trailer.licensePlate', category: 'trailer' },
      { title: 'Trailer number', value: 'trailer.number', category: 'trailer' },
      { title: 'Trailer damage', value: 'trailer.damage', category: 'trailer' },
      { title: 'Seal', value: 'trailer.hasSeal', category: 'trailer' },
      { title: 'Seal number', value: 'trailer.sealNumber', category: 'trailer' }
    ];

    const defaultStartDate = DateTime.now()
      .minus({ day: Math.floor(DEFAULT_DATE_RANGE_DAYS) })
      .toJSDate();
    const defaultEndDate = DateTime.now().toJSDate();

    const observationsList = ref(props.observations);
    const filterOptions = ref({
      searchStr: '',
      gate: '',
      direction: 'both',
      startDate: defaultStartDate,
      endDate: defaultEndDate
    });

    const selectedGate = ref(null);
    const selectedDirection = ref(null);
    const selectedWarehouse = gateFeedStore.selectedWarehouseRef;

    const selectedColumns = shallowRef(columns);

    const selectedSomeColumns = computed(() => {
      return selectedColumns.value.length > 0;
    });
    const selectedAllColumns = computed(() => {
      return selectedColumns.value.length === columns.length;
    });

    const totalItems = ref(observationsList.value.length);
    const page = ref(1);
    const footerProps = ref({
      itemsPerPageOptions: [10, 25, 50, 100],
      showFirstLastPage: true,
      showCurrentPage: true
    });

    function toggle() {
      if (selectedAllColumns.value) {
        selectedColumns.value = [];
      } else {
        selectedColumns.value = columns.slice();
      }
    }

    function search(event) {
      // Implement search logic here
      console.log('Searching for:', event.target.value);
    }

    function clearDateRange(event) {
      event.preventDefault();
      this.$refs.dateRange.setDates([]);
    }

    function dateRangeChanged([firstDate, lastDate]) {
      filterOptions.value.startDate = firstDate;
      filterOptions.value.endDate = lastDate;
    }

    return {
      gates,
      directions,
      observationsList,
      totalItems,
      page,
      footerProps,
      columns,
      filterOptions,
      selectedWarehouse,
      selectedGate,
      selectedDirection,
      selectedColumns,
      selectedSomeColumns,
      selectedAllColumns,
      search,
      toggle,
      clearDateRange,
      dateRangeChanged
    };
  }
});
</script>

<style>
.gate-feed-table table > tbody > tr > td {
  padding: 15px !important;
}
</style>
